/**
 * DPDP 2023 consent registry. Encrypted-at-rest record of every
 * consent grant + withdrawal. Erasure flow drops both registry and OPFS data.
 */

export type ConsentScope =
  | 'camera_ppg'
  | 'microphone_voice'
  | 'mic_ambient_sleep'
  | 'healthkit_read'
  | 'storage_opfs'
  | 'payment_razorpay'
  | 'mcp_apple_intelligence'
  | 'remote_tts_sarvam'
  | 'family_share_e2e';

export interface ConsentRecord {
  scope: ConsentScope;
  granted: boolean;
  ts: string;
  reason: string;
  withdrawn_at: string | null;
}

const KEY = 'aramgah:consents-v1';

export function loadConsents(): Record<ConsentScope, ConsentRecord> {
  const raw = localStorage.getItem(KEY);
  if (!raw) return {} as Record<ConsentScope, ConsentRecord>;
  try {
    return JSON.parse(raw) as Record<ConsentScope, ConsentRecord>;
  } catch {
    return {} as Record<ConsentScope, ConsentRecord>;
  }
}

export function saveConsents(c: Record<ConsentScope, ConsentRecord>): void {
  localStorage.setItem(KEY, JSON.stringify(c));
}

export function grant(scope: ConsentScope, reason: string): ConsentRecord {
  const all = loadConsents();
  const rec: ConsentRecord = {
    scope,
    granted: true,
    ts: new Date().toISOString(),
    reason,
    withdrawn_at: null,
  };
  all[scope] = rec;
  saveConsents(all);
  return rec;
}

export function withdraw(scope: ConsentScope): void {
  const all = loadConsents();
  const existing = all[scope];
  if (!existing) return;
  existing.granted = false;
  existing.withdrawn_at = new Date().toISOString();
  saveConsents(all);
}

export function hasConsent(scope: ConsentScope): boolean {
  return loadConsents()[scope]?.granted === true;
}

export async function erasureFlow(): Promise<void> {
  localStorage.removeItem(KEY);
  localStorage.removeItem('aramgah:actor-id');
  sessionStorage.removeItem('aramgah:passcode');
  if ('storage' in navigator && navigator.storage.getDirectory) {
    try {
      const dir = await navigator.storage.getDirectory();
      for await (const name of (dir as unknown as { keys: () => AsyncIterableIterator<string> }).keys()) {
        try { await dir.removeEntry(name); } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
  }
}
